import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

/**
 * Gates the whole authenticated app. Renders nothing while the initial session restore is
 * in flight — routing to /login before that settles would sign out anyone who reloads.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, initializing } = useAuth();
  const location = useLocation();

  if (initializing) {
    return <FullPageLoader />;
  }

  if (!user) {
    // `state.from` lets the login page return the user to where they were headed.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // An administrator-issued password must be changed before anything else is reachable.
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

/**
 * Deliberately minimal and text-free: this shows for a few hundred milliseconds at most,
 * and a flash of copy is worse than a flash of nothing.
 */
function FullPageLoader() {
  return (
    <div
      className="flex h-screen w-screen items-center justify-center bg-slate-50"
      role="status"
      aria-label="Loading"
    >
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-500" />
    </div>
  );
}

/**
 * Route-level permission gate. Use for a whole page; use <PermissionGate> for elements
 * inside one.
 *
 * `anyOf` is for a page that is a CONTAINER of surfaces belonging to different modules — a
 * module’s Settings menu, whose rows each carry their own key. Gating such a page on one of
 * them would hide it from a caller entitled to the others. A page over one module’s records
 * takes `permission`, always: "any of" on a single-module page is a gate that has stopped
 * saying what it protects.
 */
export function RequirePermission({
  permission,
  anyOf,
  children,
}: {
  permission?: string;
  /** Satisfied when the caller holds any one of these. */
  anyOf?: string[];
  children: ReactNode;
}) {
  const { hasPermission, hasAnyPermission } = useAuth();

  const allowed = permission ? hasPermission(permission) : anyOf ? hasAnyPermission(anyOf) : true;

  return allowed ? <>{children}</> : <Navigate to="/" replace />;
}
